import { supabase } from "./supabase";

/**
 * Shotlista: scener og bilder.
 *
 * EGNE TABELLER, ikke en tekstflate. Lista begynte som ett dokument på samme
 * maskin som beskjedene, og det holdt så lenge den var linjer med en hake.
 * Det er den ikke lenger: hvert bilde kan ha en kommentar og et
 * referansebilde, og data per linje er nettopp det et tekstdokument ikke kan
 * bære.
 *
 * PER OPPTAKSDAG. Scenene er dagens settinger, og lista skal være fersk om
 * morgenen. `shoot_day` er produksjonens eget nummer på dagen, som i
 * `log_session` — ikke en dato.
 *
 * Kaster ikke. Uten klient eller uten nett er flata tom, og det er en
 * tilstand skjermen må kunne vise.
 */

export interface Scene {
  readonly id: string;
  readonly navn: string;
  readonly sortOrder: number;
}

export interface Shot {
  readonly id: string;
  readonly sceneId: string;
  readonly tekst: string;
  readonly notat: string;
  readonly bildeSti: string | null;
  readonly gjort: boolean;
  readonly sortOrder: number;
}

export interface Shotliste {
  readonly scener: readonly Scene[];
  readonly shots: readonly Shot[];
}

export const TOM: Shotliste = { scener: [], shots: [] };

const BOTTE = "referansebilder";

function tilScene(r: Record<string, unknown>): Scene {
  return { id: String(r["id"]), navn: String(r["name"]), sortOrder: Number(r["sort_order"]) };
}

function tilShot(r: Record<string, unknown>): Shot {
  return {
    id: String(r["id"]),
    sceneId: String(r["scene_id"]),
    tekst: String(r["text"]),
    notat: String(r["note"]),
    bildeSti: r["image_path"] === null || r["image_path"] === undefined
      ? null : String(r["image_path"]),
    gjort: r["done"] === true,
    sortOrder: Number(r["sort_order"]),
  };
}

export async function hent(produksjonId: string, opptaksdag: string): Promise<Shotliste> {
  if (supabase === null) return TOM;
  const s = await supabase
    .from("shot_scene")
    .select("id, name, sort_order")
    .eq("production_id", produksjonId)
    .eq("shoot_day", opptaksdag)
    .order("sort_order", { ascending: true });
  if (s.error !== null) return TOM;
  const scener = (s.data ?? []).map((r) => tilScene(r as Record<string, unknown>));
  if (scener.length === 0) return { scener, shots: [] };

  const b = await supabase
    .from("shot")
    .select("id, scene_id, text, note, image_path, done, sort_order")
    .in("scene_id", scener.map((x) => x.id))
    .order("sort_order", { ascending: true });
  if (b.error !== null) return { scener, shots: [] };
  return { scener, shots: (b.data ?? []).map((r) => tilShot(r as Record<string, unknown>)) };
}

export async function nyScene(
  produksjonId: string, opptaksdag: string, navn: string, sortOrder: number,
): Promise<Scene | null> {
  if (supabase === null) return null;
  const { data, error } = await supabase
    .from("shot_scene")
    .insert({ production_id: produksjonId, shoot_day: opptaksdag, name: navn, sort_order: sortOrder })
    .select("id, name, sort_order")
    .single();
  if (error !== null || data === null) return null;
  return tilScene(data as Record<string, unknown>);
}

export async function endreScene(id: string, navn: string): Promise<boolean> {
  if (supabase === null) return false;
  const { error } = await supabase.from("shot_scene").update({ name: navn }).eq("id", id);
  return error === null;
}

/** Sletter scenen OG bildene i den — `on delete cascade`. */
export async function fjernScene(id: string): Promise<boolean> {
  if (supabase === null) return false;
  const { error } = await supabase.from("shot_scene").delete().eq("id", id);
  return error === null;
}

export async function nyttShot(
  sceneId: string, tekst: string, sortOrder: number,
): Promise<Shot | null> {
  if (supabase === null) return null;
  const { data, error } = await supabase
    .from("shot")
    .insert({ scene_id: sceneId, text: tekst, sort_order: sortOrder })
    .select("id, scene_id, text, note, image_path, done, sort_order")
    .single();
  if (error !== null || data === null) return null;
  return tilShot(data as Record<string, unknown>);
}

export async function endreShot(
  id: string,
  endring: Partial<{ tekst: string; notat: string; gjort: boolean; bildeSti: string | null }>,
): Promise<boolean> {
  if (supabase === null) return false;
  const rad: Record<string, unknown> = {};
  if (endring.tekst !== undefined) rad["text"] = endring.tekst;
  if (endring.notat !== undefined) rad["note"] = endring.notat;
  if (endring.gjort !== undefined) rad["done"] = endring.gjort;
  if (endring.bildeSti !== undefined) rad["image_path"] = endring.bildeSti;
  if (Object.keys(rad).length === 0) return true;
  const { error } = await supabase.from("shot").update(rad).eq("id", id);
  return error === null;
}

/** Huker av — eller av igjen — flere bilder i én runde. */
export async function settAlleGjort(
  ider: readonly string[], gjort: boolean,
): Promise<boolean> {
  if (supabase === null || ider.length === 0) return false;
  const { error } = await supabase.from("shot").update({ done: gjort }).in("id", ider);
  return error === null;
}

export async function fjernShots(ider: readonly string[]): Promise<boolean> {
  if (supabase === null || ider.length === 0) return false;
  const { error } = await supabase.from("shot").delete().in("id", ider);
  return error === null;
}

export async function fjernShot(id: string): Promise<boolean> {
  if (supabase === null) return false;
  const { error } = await supabase.from("shot").delete().eq("id", id);
  return error === null;
}

/** Lengste side på et referansebilde etter nedskalering. */
const MAKS_PIKSLER = 1600;

/**
 * Skalerer bildet ned før det sendes.
 *
 * ET REFERANSEBILDE ER EN PEKEPINN, ikke en master. Et telefonbilde på åtte
 * megabyte lastes ned i full størrelse av hver eneste telefon i felt, over
 * mobilnett — og det er ingen som ser forskjell på 1600 piksler og 4000 når
 * poenget er «sånn skal det se ut».
 *
 * `imageOrientation` er ikke pirk: uten den legger et bilde tatt på høykant
 * seg ned, fordi retningen står i EXIF og ikke i pikslene.
 *
 * Feiler noe, sendes originalen. Et bilde som kommer fram stort er bedre enn
 * et som ikke kommer fram.
 */
async function nedskaler(fil: File): Promise<{ data: Blob; type: string; endelse: string }> {
  const original = { data: fil, type: fil.type, endelse: fil.name.split(".").pop()?.toLowerCase() ?? "jpg" };
  try {
    const bilde = await createImageBitmap(fil, { imageOrientation: "from-image" });
    const storst = Math.max(bilde.width, bilde.height);
    const skala = storst > MAKS_PIKSLER ? MAKS_PIKSLER / storst : 1;
    const b = Math.round(bilde.width * skala);
    const h = Math.round(bilde.height * skala);
    const lerret = document.createElement("canvas");
    lerret.width = b;
    lerret.height = h;
    const ctx = lerret.getContext("2d");
    if (ctx === null) return original;
    ctx.drawImage(bilde, 0, 0, b, h);
    bilde.close();
    const blob = await new Promise<Blob | null>((ok) => {
      lerret.toBlob(ok, "image/jpeg", 0.82);
    });
    if (blob === null) return original;
    // Var originalen alt mindre enn det vi lagde, beholdes den.
    if (blob.size >= fil.size && skala === 1) return original;
    return { data: blob, type: "image/jpeg", endelse: "jpg" };
  } catch {
    return original;
  }
}

/**
 * Laster opp et referansebilde og returnerer stien.
 *
 * Stien har produksjonen først, slik dokumentene har: den dagen policyene
 * byttes ut, er det produksjonen de skal spørre om.
 */
export async function lastOppBilde(
  produksjonId: string, shotId: string, fil: File,
): Promise<string | null> {
  if (supabase === null) return null;
  const { data, type, endelse } = await nedskaler(fil);
  const sti = `${produksjonId}/${shotId}.${endelse}`;
  /*
   * RAA BYTES, IKKE BLOB. Se apps/mobile/src/state/rapportsynk.ts.
   *
   * `storage.upload` med en Blob bygger en FormData og legger fila i et
   * felt UTEN NAVN — `body.append("", fil)` i StorageFileApi. Paa iOS kom
   * ikke den delen med, og tjeneren svarte «No content provided» paa en
   * opplasting som hadde en gyldig fil i haanden. En ArrayBuffer tar den
   * andre veien i samme klient: raa kropp med `content-type`, og ingen
   * multipart som kan miste noe.
   *
   * Dette virker fra maskin. Det staar her fordi flata ogsaa brukes paa
   * sett, og da kan det vaere en iPad.
   */
  const { error } = await supabase.storage
    .from(BOTTE)
    .upload(sti, await data.arrayBuffer(), { upsert: true, contentType: type });
  if (error !== null) return null;
  return sti;
}

/** Skriver `sort_order` for radene som har flyttet seg. */
export async function settRekkefolge(
  tabell: "shot_scene" | "shot", ider: readonly string[],
): Promise<boolean> {
  const klient = supabase;
  if (klient === null) return false;
  const svar = await Promise.all(ider.map((id, i) =>
    klient.from(tabell).update({ sort_order: i }).eq("id", id)));
  return svar.every((r) => r.error === null);
}

/**
 * Fjerner flere referansebilder.
 *
 * Kalles naar bildene de hoerer til slettes. Uten dette ble filene liggende
 * igjen i boetta uten noe som pekte paa dem — og et bilde ingen lenger kan
 * se, men som fortsatt ligger lagret, er ikke slettet.
 */
export async function fjernBilder(stier: readonly (string | null)[]): Promise<void> {
  if (supabase === null) return;
  const ekte = stier.filter((x): x is string => x !== null);
  if (ekte.length === 0) return;
  await supabase.storage.from(BOTTE).remove(ekte);
}

export async function fjernBilde(sti: string): Promise<boolean> {
  if (supabase === null) return false;
  const { error } = await supabase.storage.from(BOTTE).remove([sti]);
  return error === null;
}

/**
 * Signert lenke til et referansebilde.
 *
 * Bøtta er ikke offentlig: veien ut går gjennom en policy, ikke gjennom en
 * gjettbar URL. Da er veien den samme før og etter innlogging, og det som
 * skal byttes er policyen alene.
 */
export async function bildeUrl(sti: string): Promise<string | null> {
  if (supabase === null) return null;
  const { data, error } = await supabase.storage.from(BOTTE).createSignedUrl(sti, 3600);
  if (error !== null || data === null) return null;
  return data.signedUrl;
}
